import { useLocation } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { useFeatureFlagsContext } from '../context/FeatureFlagContext';
import { useNotifications } from '../context/NotificationsContext.jsx';
import { useToast } from '../context/ToastContext.jsx';
import { storage } from '../utils/storage.js';

// 🏛️ Atomic UI
import { AppHeader } from '../components/snippets/app-header.snippet.jsx';
import { IdentityMenu } from '../components/blocks/identity-menu.block.jsx';
import { MessagesInboxButton } from '../components/blocks/messages-inbox.block.jsx';
import { NotificationsBell } from '../components/blocks/notifications-bell.block.jsx';
import TestUserSwitcher from '../components/blocks/test-user-switcher.block.jsx';
import { Text } from '../components/elements/text.element.jsx';

import { Button } from '../components/elements/Button.jsx';
const resolveReturnPath = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized.startsWith('/')) return '/admin/identity';
  if (normalized.startsWith('//')) return '/admin/identity';
  return normalized;
};

export default function Header({ onMenuToggle }) {
  const {
    user, logout, activeRole, availableRoles, switchRole, refreshAuth,
    personaOptions, activePersona, switchPersona, rolesInPersona,
    canPreviewAsGuest, startGuestPreview
  } = useAuthContext();
  const { flags } = useFeatureFlagsContext();
  const { items, unreadCount, markRead, refresh } = useNotifications();
  const { showToast } = useToast();
  const location = useLocation();
  const impersonationSession = storage.get(storage.KEYS.IMPERSONATION_SESSION);
  const isImpersonating = Boolean(impersonationSession?.adminAccessToken);

  const pathName = location.pathname === '/' ? 'Overview' : 
                   location.pathname.split('/').pop().replace(/-/g, ' ');

  const handleExitSupportMode = async () => {
    if (!impersonationSession?.adminAccessToken) return;
    storage.set(storage.KEYS.ACCESS_TOKEN, impersonationSession.adminAccessToken);
    if (impersonationSession.adminRefreshToken) {
      storage.set(storage.KEYS.REFRESH_TOKEN, impersonationSession.adminRefreshToken);
    } else {
      storage.remove(storage.KEYS.REFRESH_TOKEN);
    }
    const returnTo = resolveReturnPath(storage.get(storage.KEYS.IMPERSONATION_RETURN_TO));
    storage.remove(storage.KEYS.IMPERSONATION_SESSION);
    storage.remove(storage.KEYS.IMPERSONATION_RETURN_TO);
    await refreshAuth?.();
    showToast({ type: 'success', message: 'Returned to your admin session.' });
    window.history.replaceState(null, '', returnTo);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <>
      {isImpersonating && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 md:px-8">
          <div className="mx-auto flex max-w-[1600px] flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-1">
              <Text variant="label" className="text-amber-700">Support Session</Text>
              <Text variant="body" className="block text-sm text-amber-900">
                You are viewing Moksy as {impersonationSession?.targetEmail || impersonationSession?.targetUsername || impersonationSession?.targetUserId}.
              </Text>
            </div>
            <Button variant="outline" size="sm" pill={false} className="uppercase"
              onClick={handleExitSupportMode}>
              Exit Support Mode
            </Button>
          </div>
        </div>
      )}

      <AppHeader 
        onMenuToggle={onMenuToggle}
        pathName={pathName}
        isShieldActive={flags?.AI_PLAYGROUND === false}
        identity={
          <div className="flex items-center gap-4">
            <TestUserSwitcher />
            <MessagesInboxButton />
            <NotificationsBell items={items} unreadCount={unreadCount} onMarkRead={markRead} onRefresh={refresh} />
            <IdentityMenu
              user={user}
              logout={logout}
              activeRole={activeRole}
              availableRoles={availableRoles}
              switchRole={switchRole}
              personaOptions={personaOptions}
              rolesInPersona={rolesInPersona}
              activePersona={activePersona}
              switchPersona={switchPersona}
              canPreviewAsGuest={canPreviewAsGuest}
              startGuestPreview={startGuestPreview}
            />
          </div>
        }
      />
    </>
  );
}
